import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import PropTypes from 'prop-types'

import Loader from '../../../../../shared_components/NewLoader'
import BusyOverlay from './BusyOverlay'
import ChapterSwitchScreen from './ChapterSwitchScreen'
import InvitationScreen from './InvitationScreen'
import PromptScreen from './PromptScreen'
import QuestionListScreen from './QuestionListScreen'
import RestScreen from './RestScreen'
import ReviewScreen from './ReviewScreen'

import useEthicalWills from './hooks/useEthicalWills'
import useLifeStoryDraft from './hooks/useLifeStoryDraft'
import useToast from './hooks/useToast'
import {clearLifeStoryPickerRequest, lifeStoryFlowEnteredFromApp, lifeStoryPickerRequested} from './lifeStoryFlowEntry'
import {promptPosition, savedStoryEventPayload, trackLifeStoryEvent} from './lifeStoryEventLogger'
import {commitPendingDeletion, pendingLifeStoryDeletion} from './lifeStoryPendingDeletion'
import {
  LIFE_STORY_CHAPTERS,
  evergreenChapterKey,
  evergreenPrompt,
  findChapter,
  isEvergreenPromptId,
  newEvergreenPromptId
} from './lifeStoryContent'
import {
  LIFE_AND_LEGACY_CATEGORY_PATH,
  answeredStories,
  buildStory,
  latestEthicalWill,
  lifeStoryContinuePath,
  lifeStoryLockedForUser,
  nextPromptIdInChapter
} from './lifeStoryTools'
import {currentUser, currentUserConfig} from '../../../../../lib/userTools'
import {firmForEverplan} from '../../../../../lib/plan_data/userData'
import {hasMyStoryAccess} from '../../../../../pro/src/lib/firmTools'
import PremiumAccessDenied from '../../../../../shared_components/PremiumAccessDenied'
import routerUtils from '../../../../../lib/routerUtils'
import Logger from '../../../../../lib/NewLogger'

import './lifeStory.scss'

const STAGES = {
  INVITE: 'invite',
  PROMPT: 'prompt',
  SWITCH: 'switch',
  QUESTIONS: 'questions',
  REST: 'rest',
  REVIEW: 'review'
}

/**
 * Where the chapter picker was opened from, which decides where its Back button goes.
 */
const PICKER_ORIGINS = {
  ENTRY: 'entry',
  LANDING: 'landing',
  PROMPT: 'prompt',
  REVIEW: 'review'
}

/**
 * How the writer reached the first question of a chapter, which decides
 * where Back goes once there's no question history left to step through
 * (TK-3178). Any other way in (e.g. the rest screen's next chapter) falls
 * back to the landing screen.
 */
const PROMPT_ORIGINS = {
  PICKER: 'picker',
  REVIEW: 'review'
}

/** Stories answered in one sitting before the flow offers a rest (mock's chapter rest). */
export const STORIES_PER_SITTING = 3

// The next chapter in config order, wrapping. Every chapter can always take
// one more story (the evergreen open question), so no availability check.
const nextChapterKeyAfter = chapterKey => {
  const index = LIFE_STORY_CHAPTERS.findIndex(chapter => chapter.key === chapterKey)

  return LIFE_STORY_CHAPTERS[(index + 1) % LIFE_STORY_CHAPTERS.length].key
}

/**
 * Full-screen guided "My Life Story" (ethical will) authoring experience.
 *
 * `cm/life-story` starts a new story (invitation first); `cm/life-story/:ethicalWillId`
 * continues an existing one (review first). Answers autosave to the server
 * while the user writes (TK-3179) and on every way out of a question
 * (Next/Save and close); entering the review page publishes the
 * keepsake PDF and recordings to the vault.
 */
const LifeStoryFlow = props => {
  const userConfig = useSelector(state => currentUserConfig(state.api))
  const user = useSelector(state => currentUser({userConfig, users: state.api.users}))

  // The guided experience is limited to firms with My Story access — funeral
  // homes plus non-FH firms whitelisted via the my_story_enabled_firm_ids
  // Setting (TK-3197): anyone else deep-linking here is sent back to the
  // category page. Freemium clients of those firms get the premium gate
  // instead — the module is part of what they're being sold.
  const myStoryEnabled = useSelector(state => hasMyStoryAccess(firmForEverplan({
    firmOwnerships: state.api['firm-ownerships'],
    firms: state.api.firms,
    userConfig
  })))

  const locked = lifeStoryLockedForUser(userConfig)

  const {loadingEthicalWills, ethicalWillsUnavailable, ethicalWills, refetchEthicalWills} = useEthicalWills({skip: locked || !myStoryEnabled})

  // A story inside its undo-after-delete window is already gone as far as the
  // user is concerned, so a link into it (e.g. the back button) exits like any
  // other stale link. Captured at mount: the flow itself schedules deletions
  // on its way out, which must not flip an exit navigation mid-flight.
  const pendingDeletionAtMountRef = useRef(pendingLifeStoryDeletion())
  const pendingWillId = pendingDeletionAtMountRef.current ? pendingDeletionAtMountRef.current.ethicalWillId : null

  const ethicalWill = props.params.ethicalWillId && props.params.ethicalWillId !== pendingWillId ?
    ethicalWills.find(will => will.get('id') === props.params.ethicalWillId) :
    null

  // A stale deep link (deleted or foreign draft) exits back to the category
  // instead of silently dropping the user into a brand-new story. A failed
  // fetch exits too: without the collection we can't tell whether a draft
  // already exists, so starting a new one here could create a duplicate.
  const cannotResolveEthicalWill = Boolean(
    ethicalWillsUnavailable || (!loadingEthicalWills && props.params.ethicalWillId && !ethicalWill)
  )

  // One ethical will per everplan: a deep link (or back-button) into the begin
  // path must continue the existing record rather than invite a second create.
  // Checked once per visit to the begin path — a mid-session lazy create would
  // otherwise redirect to the continue URL, remount into Review, and publish
  // the brand-new draft to the vault immediately. The optional :ethicalWillId
  // route can reuse this instance for continue ↔ begin, so returning to begin
  // re-arms the guard below.
  const beginPathCheckedRef = useRef(false)
  const [beginPathExistingWill, setBeginPathExistingWill] = useState(null)

  useEffect(() => {
    if (!myStoryEnabled) routerUtils.push(LIFE_AND_LEGACY_CATEGORY_PATH)
  }, [myStoryEnabled])

  useEffect(() => {
    if (!myStoryEnabled) return

    if (cannotResolveEthicalWill) {
      routerUtils.push(LIFE_AND_LEGACY_CATEGORY_PATH)

      return
    }

    if (props.params.ethicalWillId) {
      beginPathCheckedRef.current = false
      setBeginPathExistingWill(null)

      return
    }

    if (beginPathCheckedRef.current || loadingEthicalWills || ethicalWillsUnavailable) return

    beginPathCheckedRef.current = true
    const existing = latestEthicalWill(
      pendingWillId ?
        ethicalWills.filter(will => will.get('id') !== pendingWillId) :
        ethicalWills
    )

    if (existing) {
      setBeginPathExistingWill(existing)
      // replace so Back / post-delete exit doesn't land on the begin path and
      // re-enter the continue URL while the deferred DELETE still leaves the
      // record in the store.
      routerUtils.replace(lifeStoryContinuePath(existing.get('id')))
    } else if (pendingWillId) {
      // Begin path with only a pending-delete record: commit it before the
      // invite/lazy-create path can race uniqueness against the still-live row.
      commitPendingDeletion()
    }
  }, [
    cannotResolveEthicalWill,
    myStoryEnabled,
    loadingEthicalWills,
    ethicalWillsUnavailable,
    ethicalWills,
    pendingWillId,
    props.params.ethicalWillId
  ])

  if (!myStoryEnabled)
    return <Loader loading={true} />

  if (locked)
    return <PremiumAccessDenied exit={() => routerUtils.push(LIFE_AND_LEGACY_CATEGORY_PATH)} />

  if (loadingEthicalWills || cannotResolveEthicalWill || (!props.params.ethicalWillId && beginPathExistingWill))
    return <Loader loading={true} />

  return (
    <LifeStoryFlowContent
      ethicalWill={ethicalWill}
      firstName={user.get('first-name')}
      refetchEthicalWills={refetchEthicalWills}
      userConfig={userConfig}
    />
  )
}

export const LifeStoryFlowContent = ({ethicalWill, firstName, refetchEthicalWills, userConfig}) => {
  const draft = useLifeStoryDraft({ethicalWill, firstName})

  // Every "Continue my story" outside the flow opens on the chapter picker
  // (TK-3178); read here and cleared after mount, so a StrictMode double
  // render of the initializer still sees it.
  const initialStage = () => {
    if (!ethicalWill) return STAGES.INVITE

    if (lifeStoryPickerRequested()) return STAGES.SWITCH

    return answeredStories(draft.stories).length > 0 ? STAGES.REVIEW : STAGES.SWITCH
  }

  const [stage, setStage] = useState(initialStage)
  const [currentPromptId, setCurrentPromptId] = useState(null)
  const [pickerChapterKey, setPickerChapterKey] = useState(null)
  const [pickerOrigin, setPickerOrigin] = useState(PICKER_ORIGINS.ENTRY)

  useEffect(() => { clearLifeStoryPickerRequest() }, [])

  // Only a brand-new story opens on the landing (invitation) screen; Back
  // returning there later isn't another arrival.
  useEffect(() => {
    if (stage === STAGES.INVITE) trackLifeStoryEvent({eventName: 'view_my_story_landing', userConfig})
  }, [])

  // The questions seen so far in this chapter, oldest first: Back steps to the
  // previous one (TK-3178). Starting a chapter resets it, so Back can never
  // reach into another chapter's leftovers; once it's empty, the origin of
  // the chapter's first question decides where Back goes.
  const promptHistoryRef = useRef([])
  const promptOriginRef = useRef(null)

  // Whether the current prompt was opened from Review's edit icon: a revisit
  // edits one story in place (Cancel/Save back to review) rather than moving
  // the flow forward (Back/Next).
  const [revisiting, setRevisiting] = useState(false)
  const {toast, toastMessage} = useToast()

  // Stories answered since the flow opened (or since the last rest); at
  // STORIES_PER_SITTING the flow offers the rest screen instead of the next
  // question. Skips don't count.
  const sittingCountRef = useRef(0)

  // Whether the draft has changes the vault hasn't seen: publishing on the way
  // into review is skipped when nothing changed since the last publish. Deep
  // links into an already-published story start clean.
  const dirtySincePublishRef = useRef(Boolean(ethicalWill && !ethicalWill.get('saved-to-vault-at')))

  // Whether an autosave changed the open question's words during this visit.
  // Autosaves don't mark the keepsake stale on their own (see saveAnswer),
  // and the explicit exits mark it themselves — but Back saves nothing, so
  // it has to carry the change the autosaves already made (TK-3178).
  const autosavedChangesRef = useRef(false)

  // The flow is its own scroll container (fixed, overflow-y: auto), so a new
  // screen is scrolled to its top here rather than through `window`.
  const flowRef = useRef(null)

  const logEvent = useCallback((name, payload = {}) => {
    Logger.log({name, payload: {context: 'life_story_flow', type: 'vault', ...payload}})
  }, [])

  const trackEvent = useCallback((eventName, payload = {}) => {
    trackLifeStoryEvent({eventName, userConfig, payload})
  }, [userConfig])

  const storyForPrompt = promptId => draft.stories.find(story => story['prompt-id'] === promptId)

  const chapterKeyForPrompt = promptId =>
    evergreenChapterKey(promptId) ||
    LIFE_STORY_CHAPTERS.find(chapter => chapter.prompts.some(prompt => prompt.id === promptId)).key

  const scrollToTop = useCallback(() => {
    if (flowRef.current) flowRef.current.scrollTop = 0
  }, [])

  const showStage = useCallback(nextStage => {
    setStage(nextStage)
    scrollToTop()
  }, [scrollToTop])

  // The prompt the chapter caret was pressed on, with its Back context, so
  // the picker's Back returns there even after another question was chosen
  // and left along the way (choosing one makes it the current prompt).
  const pickerReturnRef = useRef(null)

  const openSwitch = useCallback(origin => {
    pickerReturnRef.current = origin === PICKER_ORIGINS.PROMPT ?
      {
        promptId: currentPromptId,
        revisiting,
        origin: promptOriginRef.current,
        history: [...promptHistoryRef.current]
      } :
      null
    setPickerOrigin(origin)
    showStage(STAGES.SWITCH)
  }, [currentPromptId, revisiting, showStage])

  const showQuestionList = useCallback(chapterKey => {
    setPickerChapterKey(chapterKey)
    showStage(STAGES.QUESTIONS)
  }, [showStage])

  // Choosing a chapter counts as opening it; stepping Back to its question
  // list from a prompt (showQuestionList) doesn't.
  const openQuestionList = useCallback(chapterKey => {
    showQuestionList(chapterKey)
    logEvent('opened_life_story_chapter', {wildcard: chapterKey})
    trackEvent('click_my_story_chapter')
  }, [logEvent, showQuestionList, trackEvent])

  // The invitation's and Review's chapter tiles are the picker's first step,
  // so the question list's Back returns to the screen the tile was on.
  const openQuestionListFromLanding = useCallback(chapterKey => {
    setPickerOrigin(PICKER_ORIGINS.LANDING)
    openQuestionList(chapterKey)
  }, [openQuestionList])

  const openQuestionListFromReview = useCallback(chapterKey => {
    setPickerOrigin(PICKER_ORIGINS.REVIEW)
    openQuestionList(chapterKey)
  }, [openQuestionList])

  const showPrompt = useCallback(promptId => {
    autosavedChangesRef.current = false
    setCurrentPromptId(promptId)
    setRevisiting(false)
    showStage(STAGES.PROMPT)
  }, [showStage])

  // Moving forward within a chapter (Next, the rest screen's continue)
  // records the question being left so Back can return to it.
  const showNextPrompt = useCallback(promptId => {
    if (currentPromptId) promptHistoryRef.current.push(currentPromptId)

    showPrompt(promptId)
  }, [currentPromptId, showPrompt])

  const startChapterAt = useCallback(({promptId, origin = null}) => {
    promptHistoryRef.current = []
    promptOriginRef.current = origin
    showPrompt(promptId)
  }, [showPrompt])

  // Entering a chapter fresh without choosing a question (the rest screen's
  // next chapter) revisits skipped questions once the untouched ones run out —
  // a skipped question is still unanswered (TK-2983 AC 6.4).
  const openChapter = useCallback(chapterKey => {
    startChapterAt({promptId: nextPromptIdInChapter({chapterKey, stories: draft.stories, revisitSkipped: true})})
    logEvent('opened_life_story_chapter', {wildcard: chapterKey})
    trackEvent('click_my_story_chapter')
  }, [draft.stories, logEvent, startChapterAt, trackEvent])

  const pickPrompt = useCallback(promptId => {
    startChapterAt({promptId, origin: PROMPT_ORIGINS.PICKER})
    trackEvent('click_my_story_question')
  }, [startChapterAt, trackEvent])

  const pickEvergreen = useCallback(chapterKey => pickPrompt(newEvergreenPromptId(chapterKey)), [pickPrompt])

  // Review's "+ Add a story" drawer opens a chosen question straight from Review.
  const openPromptFromReview = useCallback(promptId => {
    startChapterAt({promptId, origin: PROMPT_ORIGINS.REVIEW})
    trackEvent('click_my_story_question')
  }, [startChapterAt, trackEvent])

  // There is no Skip button (TK-3178); a Next with an empty answer is the
  // skip, and it says nothing (TK-2983 AC 3.8) — the question is recorded as
  // skipped so it isn't offered again right away, and the next one appears.
  const advanceFromPrompt = useCallback(({promptId, answered}) => {
    if (!answered) trackEvent('skip_prompt', promptPosition(promptId))

    if (answered) {
      toast('Saved. Your story is growing.')
      dirtySincePublishRef.current = true
      sittingCountRef.current += 1

      // The open "anything else…" question closes out its chapter, so answering
      // it rests right away — the alternative is asking the same question again
      // (and again) until the sitting count fills.
      if (sittingCountRef.current >= STORIES_PER_SITTING || isEvergreenPromptId(promptId)) {
        sittingCountRef.current = 0
        showStage(STAGES.REST)

        return
      }
    } else if (isEvergreenPromptId(promptId)) {
      // Skipping the open "anything else…" question is the one place the
      // chapter has nothing new to offer: re-asking the question they just
      // skipped would feel broken, so offer the chapter picker instead.
      openSwitch(PICKER_ORIGINS.PROMPT)

      return
    }

    // An empty Next stays inside the chapter (TK-2983 AC 3.4): the next
    // unanswered question in configured order, never a jump to another chapter.
    showNextPrompt(nextPromptIdInChapter({
      chapterKey: chapterKeyForPrompt(promptId),
      stories: draft.stories,
      afterPromptId: promptId
    }))
  }, [draft.stories, openSwitch, showNextPrompt, showStage, toast, trackEvent])

  // `options` (TK-3179): `autosave` marks a save the writer didn't ask for
  // (timer, blur, tab hide, a revisit's Cancel putting the words back) — it
  // keeps the server current but doesn't by itself make the keepsake stale,
  // so review isn't pushed through a publish the user never triggered
  // (TK-3061). The explicit exits (Next, Save, Save and close…) still mark
  // it. `keepalive` passes through to the draft for the page-unload save.
  // `finished` marks the ways out of a question (Next, Save, Save and close,
  // a chapter switch): only those count as saving a story for Mixpanel, not
  // the in-place saves along the way (a recording landing or removed).
  const saveAnswer = useCallback(({chapter, prompt, text, recording, draftRecording, replacedRecording, photos, skipped = false}, options = {}) => {
    if (!skipped && !options.autosave) dirtySincePublishRef.current = true

    // An autosave that changes the words is remembered for Back (recordings
    // are never autosaved: they commit through an explicit save).
    if (options.autosave) {
      const previous = storyForPrompt(prompt.id)

      if (((previous && previous.text) || '') !== (text || '')) autosavedChangesRef.current = true
    }

    // A skipped configured prompt is recorded so it isn't offered again, but
    // skipping a fresh evergreen "anything else…" prompt records nothing: its
    // one-off id would never be offered again, so the record is pure noise.
    if (skipped && isEvergreenPromptId(prompt.id) && !storyForPrompt(prompt.id))
      return Promise.resolve()

    const story = buildStory({chapter, prompt, text, recording, draftRecording, replacedRecording, photos, skipped})

    // The saves the My Story usage reports rank chapters and questions by
    // (TK-3233): `question_number` is "chapter-question", counted from 1.
    if (options.finished && answeredStories([story]).length > 0) {
      const {chapter_number: chapterNumber, question_number: questionNumber} = promptPosition(prompt.id)

      trackEvent('save_my_story', savedStoryEventPayload({stories: draft.stories, story}))
      logEvent('saved_life_story_chapter', {
        wildcard: story['chapter-key'],
        ...chapterNumber ? {question_number: `${chapterNumber}-${questionNumber}`} : {}
      })
    }

    return draft.saveStory(story, {keepalive: Boolean(options.keepalive)})
  }, [draft.saveStory, draft.stories, logEvent, trackEvent])

  // Exits return to wherever the user entered from (the Dashboard or Life &
  // Legacy hero): the flow never rewrites the URL mid-session, so history
  // back lands on the entry page. Going back is only safe when this document
  // actually navigated here from another in-app screen — behind any other
  // entry (a post-login redirect chain, a restored mobile tab, a fresh deep
  // link) sit single-shot auth interstitials that wipe the session and strand
  // the user on "Logging Out..." (TK-3070) — so those exits push the category
  // page instead.
  //
  // After a full-story delete the record is only pending (DELETE waits on the
  // undo window), so goBack can re-resolve that same will and bounce straight
  // back into Review until the timer fires. Always leave for the category.
  const exitFlow = useCallback((options = {}) => {
    refetchEthicalWills()

    if (!options.afterDelete && lifeStoryFlowEnteredFromApp())
      routerUtils.goBack()
    else
      routerUtils.push(LIFE_AND_LEGACY_CATEGORY_PATH)
  }, [refetchEthicalWills])

  /**
   * The gateway to the review page: publishes the draft to the vault (PDF +
   * recordings) when it changed since the last publish, so review always
   * reflects what loved ones would receive.
   */
  const enterReview = useCallback(() => {
    if (!dirtySincePublishRef.current || answeredStories(draft.stories).length === 0) {
      showStage(STAGES.REVIEW)

      return Promise.resolve()
    }

    logEvent('saved_life_story_to_vault')

    return draft.publish()
      .then(() => {
        dirtySincePublishRef.current = false
        refetchEthicalWills()
        showStage(STAGES.REVIEW)
      })
      .catch(() => toast('We couldn\u2019t save your story to your Everplan just now. Your answers are safe \u2014 try again in a moment.'))
  }, [draft.publish, draft.stories, refetchEthicalWills, showStage, toast, logEvent])

  // Back from a question is neutral — nothing is saved or marked skipped
  // (PromptScreen drops the unsaved words). It steps to the previous question
  // seen in this chapter, else to wherever the chapter was entered from.
  // The words autosave already changed stay, though, so they count against
  // the keepsake like an explicit save would: Back to Review goes through
  // the publish gateway rather than straight to the page.
  const backFromPrompt = useCallback(() => {
    if (autosavedChangesRef.current) {
      autosavedChangesRef.current = false
      dirtySincePublishRef.current = true
    }

    if (promptHistoryRef.current.length > 0) {
      showPrompt(promptHistoryRef.current.pop())

      return
    }

    if (promptOriginRef.current === PROMPT_ORIGINS.PICKER)
      showQuestionList(chapterKeyForPrompt(currentPromptId))
    else if (promptOriginRef.current === PROMPT_ORIGINS.REVIEW)
      enterReview()
    else
      showStage(STAGES.INVITE)
  }, [currentPromptId, enterReview, showPrompt, showQuestionList, showStage])

  // A deep link into review (continuing a never-published draft) still owes
  // the vault a publish so the page reflects what loved ones would receive.
  const publishedOnEntryRef = useRef(false)

  useEffect(() => {
    if (publishedOnEntryRef.current || stage !== STAGES.REVIEW || !dirtySincePublishRef.current) return

    publishedOnEntryRef.current = true
    enterReview()
  }, [stage, enterReview])

  // Every way back onto the review page goes through enterReview: the draft
  // may have been marked stale on the way (an answer, an autosave Back
  // carried), and only the gateway refreshes what loved ones would receive.
  // Restores the whole snapshot, not just the prompt: choosing a question
  // from the picker (even the same one) has already reset the Back history,
  // origin and revisit state through startChapterAt.
  const returnToPickerPrompt = useCallback(() => {
    const {promptId, revisiting: wasRevisiting, origin, history} = pickerReturnRef.current

    promptOriginRef.current = origin
    promptHistoryRef.current = history
    setCurrentPromptId(promptId)
    setRevisiting(wasRevisiting)
    showStage(STAGES.PROMPT)
  }, [showStage])

  const backFromSwitch = useCallback(() => {
    if (pickerOrigin === PICKER_ORIGINS.PROMPT && pickerReturnRef.current && pickerReturnRef.current.promptId)
      returnToPickerPrompt()
    else if (pickerOrigin === PICKER_ORIGINS.LANDING)
      showStage(STAGES.INVITE)
    else if (pickerOrigin === PICKER_ORIGINS.REVIEW || answeredStories(draft.stories).length > 0)
      enterReview()
    else
      exitFlow()
  }, [draft.stories, enterReview, exitFlow, pickerOrigin, returnToPickerPrompt, showStage])

  // Reached from the landing or Review tiles, the question list stands in for
  // the chapter list, so its Back returns to that screen; otherwise it steps
  // back to all chapters. The chapter caret always opens the list.
  const questionListBackStage = {
    [PICKER_ORIGINS.LANDING]: STAGES.INVITE,
    [PICKER_ORIGINS.REVIEW]: STAGES.REVIEW
  }[pickerOrigin]

  const backFromQuestionList = useCallback(() => {
    if (questionListBackStage === STAGES.REVIEW) enterReview()
    else showStage(questionListBackStage || STAGES.SWITCH)
  }, [enterReview, questionListBackStage, showStage])

  const openChapterList = useCallback(() => showStage(STAGES.SWITCH), [showStage])

  const continueStory = useCallback(() => openSwitch(PICKER_ORIGINS.REVIEW), [openSwitch])

  const revisitPrompt = useCallback(promptId => {
    showPrompt(promptId)
    setRevisiting(true)
  }, [showPrompt])

  // Cancelling a revisit backs straight out to review: nothing was saved, so
  // there's nothing to publish and no "Saving your story…" wait (TK-3061).
  const cancelRevisit = useCallback(() => {
    showStage(STAGES.REVIEW)
  }, [showStage])

  const removeStory = useCallback(promptId => {
    logEvent('deleted_life_story_prompt', {wildcard: 'owner'})
    dirtySincePublishRef.current = true

    return draft.removeStory(promptId).then(() => enterReview())
  }, [draft.removeStory, enterReview, logEvent])

  const currentPromptConfig = useMemo(() => {
    if (!currentPromptId) return null

    const evergreenKey = evergreenChapterKey(currentPromptId)

    if (evergreenKey) {
      const evergreenChapter = findChapter(evergreenKey)

      return {chapter: evergreenChapter, prompt: evergreenPrompt({chapter: evergreenChapter, promptId: currentPromptId})}
    }

    const chapter = LIFE_STORY_CHAPTERS.find(candidateChapter => candidateChapter.prompts.some(candidatePrompt => candidatePrompt.id === currentPromptId))
    const prompt = chapter.prompts.find(candidatePrompt => candidatePrompt.id === currentPromptId)

    return {chapter, prompt}
  }, [currentPromptId])

  const restChapterKey = currentPromptId ? chapterKeyForPrompt(currentPromptId) : LIFE_STORY_CHAPTERS[0].key

  const restContinueChapter = useCallback(() => {
    showNextPrompt(nextPromptIdInChapter({
      chapterKey: restChapterKey,
      stories: draft.stories,
      afterPromptId: currentPromptId
    }))
  }, [draft.stories, restChapterKey, currentPromptId, showNextPrompt])

  const restBeginNextChapter = useCallback(() => {
    openChapter(nextChapterKeyAfter(restChapterKey))
  }, [restChapterKey, openChapter])

  // The story screen's fresh copy of the will (first-group-id, created-at)
  // lands in the redux store on refetch; useEthicalWills reads it without a
  // second request.
  const {ethicalWills: storedEthicalWills} = useEthicalWills({skip: true})
  const reviewEthicalWill = draft.ethicalWillId ?
    storedEthicalWills.find(will => will.get('id') === draft.ethicalWillId) || ethicalWill :
    ethicalWill

  return (
    <div className='life-story-flow' ref={flowRef}>
      <div className='ls-blob ls-blob-1' />
      <div className='ls-blob ls-blob-2' />
      {stage !== STAGES.PROMPT && <div className='ls-blob ls-blob-3' />}
      {
        stage === STAGES.INVITE &&
          <InvitationScreen exit={exitFlow} startChapter={openQuestionListFromLanding} />
      }
      {
        stage === STAGES.PROMPT && currentPromptConfig &&
          <PromptScreen
            key={currentPromptId}
            chapter={currentPromptConfig.chapter}
            prompt={currentPromptConfig.prompt}
            story={storyForPrompt(currentPromptId)}
            publishing={draft.publishing}
            saveStatus={draft.saveStatus}
            lastSavedAt={draft.lastSavedAt}
            logEvent={logEvent}
            retrySave={draft.retrySave}
            userConfig={userConfig}
            revisiting={revisiting}
            cancelRevisit={cancelRevisit}
            saveAnswer={saveAnswer}
            advance={({answered}) => advanceFromPrompt({promptId: currentPromptId, answered})}
            back={backFromPrompt}
            openChapterSwitch={() => openSwitch(PICKER_ORIGINS.PROMPT)}
            saveAndClose={enterReview}
            toast={toast}
          />
      }
      {
        stage === STAGES.SWITCH &&
          <ChapterSwitchScreen
            stories={draft.stories}
            openChapter={openQuestionList}
            back={backFromSwitch}
          />
      }
      {
        stage === STAGES.QUESTIONS && pickerChapterKey &&
          <QuestionListScreen
            chapterKey={pickerChapterKey}
            stories={draft.stories}
            openPrompt={pickPrompt}
            openEvergreen={() => pickEvergreen(pickerChapterKey)}
            openChapterList={openChapterList}
            back={backFromQuestionList}
            backLabel={questionListBackStage ? 'Back' : 'All chapters'}
          />
      }
      {
        stage === STAGES.REST &&
          <RestScreen
            chapterName={findChapter(restChapterKey).name}
            continueChapter={restContinueChapter}
            beginNextChapter={restBeginNextChapter}
            takeBreak={enterReview}
          />
      }
      {
        stage === STAGES.REVIEW &&
          <ReviewScreen
            continueStory={continueStory}
            ethicalWill={reviewEthicalWill}
            ethicalWillId={draft.ethicalWillId}
            exit={exitFlow}
            openChapter={openQuestionListFromReview}
            openPrompt={openPromptFromReview}
            removeStory={removeStory}
            revisitPrompt={revisitPrompt}
            stories={draft.stories}
            title={draft.title}
            toast={toast}
            userConfig={userConfig}
          />
      }
      {draft.publishing && <BusyOverlay message={'Saving your story to your Everplan\u2026'} />}
      {toastMessage && <div className='ls-toast' role='status'>{toastMessage}</div>}
    </div>
  )
}

LifeStoryFlow.propTypes = {
  params: PropTypes.shape({
    ethicalWillId: PropTypes.string
  })
}

LifeStoryFlowContent.propTypes = {
  ethicalWill: PropTypes.object,
  firstName: PropTypes.string,
  refetchEthicalWills: PropTypes.func,
  userConfig: PropTypes.object
}

export default LifeStoryFlow
